// src/pages/public/CourseGroupsDetailsPage.jsx
import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MemoryIcon from '@mui/icons-material/Memory';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PaletteIcon from '@mui/icons-material/Palette';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useSearchParams, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import { COURSE_GROUPS } from '../../data/courseGroupsData';
import LandingNavbar from '../../components/landing/LandingNavbar';
import FooterSection from '../../components/landing/FooterSection';
import FloatingAiCoursesButton from '../../components/landing/FloatingAiCoursesButton';
import { franchiseEnquiryAPI } from '../../services/api';
import { toast } from 'react-toastify';

const ICON_MAP = {
  Memory: MemoryIcon,
  Security: SecurityIcon,
  BusinessCenter: BusinessCenterIcon,
  Palette: PaletteIcon,
  Psychology: PsychologyIcon,
};

const districts = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur',
  'Palakkad', 'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod',
];

const emptyFranchise = {
  name: '', institutionName: '', email: '', phone: '',
  city: '', place: '', district: '', address: '', pincode: '', message: '',
};

export default function CourseGroupsDetailsPage() {
  const { mode } = useContext(ThemeContext);
  const isDark = mode === 'dark';
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const initialGroup = searchParams.get('group') || 'all';
  const [selectedGroupTab, setSelectedGroupTab] = useState(initialGroup);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedCourseForEnquiry, setSelectedCourseForEnquiry] = useState(null);
  const [franchiseForm, setFranchiseForm] = useState(emptyFranchise);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const groupParam = searchParams.get('group');
    if (groupParam) {
      setSelectedGroupTab(groupParam);
    }
  }, [searchParams]);

  const handleFranchiseSubmit = async (e) => {
    e.preventDefault();
    try {
      await franchiseEnquiryAPI.create(franchiseForm);
      toast.success('Enquiry submitted! Our representative will contact you shortly.');
      setEnquiryOpen(false);
      setFranchiseForm(emptyFranchise);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error(error.response?.data?.message || 'Failed to submit enquiry.');
    }
  };

  const handleOpenCourseEnquiry = (courseName, groupTitle) => {
    setSelectedCourseForEnquiry({ courseName, groupTitle });
    setFranchiseForm((prev) => ({
      ...prev,
      message: `Enquiry for Course: ${courseName} under ${groupTitle}`,
    }));
    setEnquiryOpen(true);
  };

  // Filter logic
  const filteredGroups = useMemo(() => {
    return COURSE_GROUPS.map((group) => {
      if (selectedGroupTab !== 'all' && group.id !== selectedGroupTab) {
        return null;
      }

      const filteredCategories = group.categories.map((category) => {
        if (selectedCategoryTab !== 'all' && category.categoryName !== selectedCategoryTab) {
          return null;
        }

        const filteredCourses = category.courses.filter((course) => {
          if (!searchQuery) return true;
          const q = searchQuery.toLowerCase();
          return (
            course.name.toLowerCase().includes(q) ||
            (course.desc && course.desc.toLowerCase().includes(q)) ||
            (course.tag && course.tag.toLowerCase().includes(q))
          );
        });

        if (filteredCourses.length === 0) return null;

        return {
          ...category,
          courses: filteredCourses,
        };
      }).filter(Boolean);

      if (filteredCategories.length === 0) return null;

      return {
        ...group,
        categories: filteredCategories,
      };
    }).filter(Boolean);
  }, [selectedGroupTab, selectedCategoryTab, searchQuery]);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: isDark ? '#fff' : '#0f172a',
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' },
      '&.Mui-focused fieldset': { borderColor: isDark ? '#38bdf8' : '#1d6cf0' },
    },
    '& .MuiInputLabel-root': { color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#38bdf8' : '#1d6cf0' },
    '& .MuiSelect-icon': { color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' },
  };

  const dialogPaperSx = {
    bgcolor: isDark ? '#0d1627' : '#ffffff',
    color: isDark ? '#fff' : '#0f172a',
    borderRadius: 4,
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
  };

  return (
    <Box sx={{ bgcolor: isDark ? '#080c16' : '#f8fafc', minHeight: '100vh', color: isDark ? '#fff' : '#0f172a', transition: 'all 0.3s ease' }}>
      <LandingNavbar
        onEnroll={() => setEnquiryOpen(true)}
        onFranchise={() => setEnquiryOpen(true)}
      />

      {/* Hero Header Banner */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(180deg, #0b1329 0%, #080c16 100%)'
            : 'linear-gradient(180deg, #e2e8f0 0%, #f8fafc 100%)',
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              color: isDark ? '#38bdf8' : '#1d6cf0',
              mb: 3,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              '&:hover': { bgcolor: isDark ? 'rgba(56, 189, 248, 0.1)' : 'rgba(29, 108, 240, 0.08)' },
            }}
          >
            Back to Home
          </Button>

          <Typography
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              letterSpacing: '-0.5px',
              color: isDark ? '#fff' : '#0f172a',
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            BSS IT Mission <Box component="span" sx={{ color: isDark ? '#38bdf8' : '#1d6cf0' }}>School Groups</Box> Catalog
          </Typography>
          <Typography
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569',
              fontSize: { xs: '1rem', md: '1.1rem' },
              maxWidth: 840,
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            Explore all 5 specialized technology schools & government-recognized diploma and certificate courses listed in our official curriculum.
          </Typography>

          {/* Search Input */}
          <Box sx={{ maxWidth: 640 }}>
            <TextField
              fullWidth
              placeholder="Search by course name, topic, or keyword (e.g. Python, AI, Cyber Security, Tally, UI/UX)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isDark ? '#38bdf8' : '#1d6cf0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                borderRadius: 3,
                boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.06)',
                '& .MuiOutlinedInput-root': {
                  color: isDark ? '#fff' : '#0f172a',
                  borderRadius: 3,
                  '& fieldset': { borderColor: isDark ? 'rgba(56, 189, 248, 0.3)' : 'rgba(29, 108, 240, 0.25)' },
                  '&:hover fieldset': { borderColor: isDark ? '#38bdf8' : '#1d6cf0' },
                  '&.Mui-focused fieldset': { borderColor: isDark ? '#38bdf8' : '#1d6cf0', borderWidth: '2px' },
                },
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Main Content Container */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Group Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0', mb: 4 }}>
          <Tabs
            value={selectedGroupTab}
            onChange={(e, val) => setSelectedGroupTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                py: 2,
                px: 3,
                '&.Mui-selected': {
                  color: isDark ? '#38bdf8' : '#1d6cf0',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: isDark ? '#38bdf8' : '#1d6cf0',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab label="All Groups" value="all" />
            {COURSE_GROUPS.map((g) => (
              <Tab key={g.id} label={`Group ${g.groupNumber}: ${g.shortTitle}`} value={g.id} />
            ))}
          </Tabs>
        </Box>

        {/* Category Filter Chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 5 }}>
          {[
            { label: 'All Course Types', value: 'all' },
            { label: 'Diploma Courses', value: 'Diploma Courses' },
            { label: 'Professional Career Diplomas', value: 'Professional Career Diploma Programmes' },
            { label: 'Certificate Courses', value: 'Certificate Courses' },
          ].map((cat) => (
            <Chip
              key={cat.value}
              label={cat.label}
              onClick={() => setSelectedCategoryTab(cat.value)}
              sx={{
                bgcolor: selectedCategoryTab === cat.value
                  ? (isDark ? '#1d6cf0' : '#1d6cf0')
                  : (isDark ? 'rgba(255, 255, 255, 0.06)' : '#ffffff'),
                color: selectedCategoryTab === cat.value
                  ? '#fff'
                  : (isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569'),
                border: '1px solid',
                borderColor: selectedCategoryTab === cat.value
                  ? (isDark ? '#38bdf8' : '#1d6cf0')
                  : (isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'),
                fontWeight: 700,
                fontSize: '0.82rem',
                py: 2,
                px: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: (selectedCategoryTab !== cat.value && !isDark) ? '0 2px 5px rgba(0,0,0,0.03)' : 'none',
                '&:hover': {
                  bgcolor: selectedCategoryTab === cat.value
                    ? '#1d6cf0'
                    : (isDark ? 'rgba(255, 255, 255, 0.12)' : '#f1f5f9'),
                },
              }}
            />
          ))}
        </Stack>

        {/* Display Groups */}
        {filteredGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10, bgcolor: isDark ? 'rgba(15, 23, 42, 0.5)' : '#ffffff', borderRadius: 4, border: isDark ? 'none' : '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569', mb: 1 }}>
              No courses found matching your search.
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8' }}>
              Try searching with another keyword or resetting the filter tabs.
            </Typography>
          </Box>
        ) : (
          filteredGroups.map((group) => {
            const IconComponent = ICON_MAP[group.icon] || SchoolIcon;

            return (
              <Box key={group.id} sx={{ mb: 8 }}>
                {/* Group School Banner */}
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    mb: 4,
                    boxShadow: isDark ? '0 12px 35px rgba(0,0,0,0.5)' : '0 12px 30px rgba(0,0,0,0.1)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    component="img"
                    src={group.image}
                    alt={group.title}
                    sx={{
                      width: '100%',
                      height: { xs: 200, md: 240 },
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg, rgba(9, 13, 26, 0.4) 0%, rgba(9, 13, 26, 0.92) 100%)',
                    }}
                  />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 4 }, zIndex: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(8px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip
                            label={`GROUP ${group.groupNumber}`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(0, 0, 0, 0.65)',
                              color: '#fff',
                              fontWeight: 900,
                              fontSize: '0.72rem',
                            }}
                          />
                          <Chip
                            label={group.badge}
                            size="small"
                            sx={{
                              bgcolor: group.accentColor,
                              color: '#fff',
                              fontWeight: 800,
                              fontSize: '0.72rem',
                            }}
                          />
                        </Stack>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#fff', mb: 0.5 }}>
                          {group.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                          {group.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                {/* Group Categories */}
                {group.categories.map((category) => (
                  <Box key={category.categoryName} sx={{ mb: 5 }}>
                    <Typography
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 800,
                        color: isDark ? '#38bdf8' : '#1d6cf0',
                        mb: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: '1.3rem' }} />
                      {category.categoryName} ({category.courses.length})
                    </Typography>

                    <Grid container spacing={3}>
                      {category.courses.map((course, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Card
                            sx={{
                              bgcolor: isDark ? 'rgba(15, 23, 42, 0.85)' : '#ffffff',
                              color: isDark ? '#fff' : '#0f172a',
                              borderRadius: 3.5,
                              border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                              boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.04)',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.25s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: group.accentColor,
                                boxShadow: isDark
                                  ? `0 12px 30px -10px ${group.borderGlow}`
                                  : `0 12px 25px -8px ${group.borderGlow}`,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Chip
                                  label={course.tag || 'Certified'}
                                  size="small"
                                  sx={{
                                    bgcolor: isDark ? `${group.accentColor}22` : `${group.accentColor}15`,
                                    color: group.accentColor,
                                    border: `1px solid ${group.accentColor}44`,
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
                                  <AccessTimeIcon sx={{ fontSize: '0.9rem' }} />
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                    {course.duration}
                                  </Typography>
                                </Stack>
                              </Box>

                              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 1, lineHeight: 1.3 }}>
                                {course.name}
                              </Typography>

                              <Typography sx={{ fontSize: '0.82rem', color: isDark ? 'rgba(255,255,255,0.6)' : '#64748b', lineHeight: 1.6, mb: 2.5, flex: 1 }}>
                                {course.desc || 'Government-approved curriculum designed for practical industry readiness.'}
                              </Typography>

                              <Button
                                fullWidth
                                onClick={() => handleOpenCourseEnquiry(course.name, group.title)}
                                variant="outlined"
                                endIcon={<SendIcon sx={{ fontSize: '0.85rem !important' }} />}
                                sx={{
                                  color: isDark ? '#fff' : '#0f172a',
                                  borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                  borderRadius: 2.5,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  py: 0.9,
                                  mt: 'auto',
                                  '&:hover': {
                                    borderColor: group.accentColor,
                                    bgcolor: isDark ? `${group.accentColor}18` : `${group.accentColor}10`,
                                  },
                                }}
                              >
                                Request Details / Enroll
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            );
          })
        )}
      </Container>

      {/* Footer */}
      <FooterSection />
      <FloatingAiCoursesButton />

      {/* Course Enquiry Modal */}
      <Dialog
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: isDark ? '#38bdf8' : '#1d6cf0' }}>
          Course Admission Enquiry
        </DialogTitle>
        <DialogContent>
          {selectedCourseForEnquiry && (
            <Box sx={{ mb: 3, p: 2, bgcolor: isDark ? 'rgba(56, 189, 248, 0.1)' : 'rgba(29, 108, 240, 0.08)', borderRadius: 2, border: isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(29, 108, 240, 0.2)' }}>
              <Typography sx={{ color: isDark ? '#fff' : '#0f172a', fontWeight: 800, fontSize: '0.95rem' }}>
                Course: {selectedCourseForEnquiry.courseName}
              </Typography>
              <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#475569', fontSize: '0.8rem' }}>
                {selectedCourseForEnquiry.groupTitle}
              </Typography>
            </Box>
          )}

          <Typography align="center" sx={{ mb: 3, color: isDark ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
            Fill in your contact information below and our team will get in touch with you about enrollment, fees, and batch schedules.
          </Typography>

          <Box component="form" onSubmit={handleFranchiseSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" name="name"
                  value={franchiseForm.name} onChange={(e) => setFranchiseForm((prev) => ({ ...prev, name: e.target.value }))}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" name="phone"
                  value={franchiseForm.phone} onChange={(e) => setFranchiseForm((prev) => ({ ...prev, phone: e.target.value }))}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" name="email" type="email"
                  value={franchiseForm.email} onChange={(e) => setFranchiseForm((prev) => ({ ...prev, email: e.target.value }))}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="District" name="district"
                  value={franchiseForm.district} onChange={(e) => setFranchiseForm((prev) => ({ ...prev, district: e.target.value }))}
                  required sx={inputSx}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: isDark ? '#0d1627' : '#ffffff', color: isDark ? '#fff' : '#0f172a' } } } }}
                >
                  {districts.map((d) => (
                    <MenuItem key={d} value={d} sx={{ color: isDark ? '#fff' : '#0f172a', '&:hover': { bgcolor: isDark ? 'rgba(56,189,248,0.1)' : 'rgba(29,108,240,0.08)' } }}>
                      {d}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Message / Additional Queries" name="message"
                  value={franchiseForm.message} onChange={(e) => setFranchiseForm((prev) => ({ ...prev, message: e.target.value }))}
                  multiline rows={3}
                  sx={inputSx} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 1 }}>
          <Button
            onClick={() => setEnquiryOpen(false)}
            variant="outlined"
            sx={{ color: isDark ? '#fff' : '#0f172a', borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFranchiseSubmit}
            variant="contained"
            sx={{ background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 2, px: 3 }}
          >
            Submit Course Enquiry
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
